import * as Sentry from '@sentry/react';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN?.trim();
const environment = import.meta.env.VITE_APP_ENV?.trim() || 'development';
const release = import.meta.env.VITE_APP_VERSION?.trim();
const sentryEnabled = Boolean(sentryDsn);
const SERVICE_TAG = 'web';

type EventPayload = Record<string, unknown>;
type EventTags = Record<string, string>;

const scrubSensitiveHeaders = (
  headers: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined => {
  if (!headers) {
    return headers;
  }

  const copy: Record<string, unknown> = { ...headers };
  delete copy.Authorization;
  delete copy.authorization;
  delete copy.cookie;
  delete copy.Cookie;
  return copy;
};

const getRequestIdFromContext = (context?: EventPayload): string | null => {
  if (!context) {
    return null;
  }

  const candidateKeys = ['requestId', 'responseRequestId', 'sentRequestId'];
  for (const key of candidateKeys) {
    const value = context[key];
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }

  return null;
};

export const isSentryEnabled = (): boolean => sentryEnabled;

export const initSentryWeb = (): void => {
  if (!sentryEnabled) {
    return;
  }

  Sentry.init({
    dsn: sentryDsn,
    environment,
    release,
    tracesSampleRate: 0,
    beforeSend(event) {
      if (event.request?.headers && typeof event.request.headers === 'object') {
        event.request.headers = scrubSensitiveHeaders(
          event.request.headers as Record<string, unknown>,
        ) as { [key: string]: string };
      }

      event.tags = {
        ...event.tags,
        service: SERVICE_TAG,
      };

      return event;
    },
  });
};

export const setSentryUser = (user: { id: string; email?: string | null } | null): void => {
  if (!sentryEnabled) {
    return;
  }

  Sentry.setUser(
    user
      ? {
          id: user.id,
          email: user.email ?? undefined,
        }
      : null,
  );
};

export const addSentryBreadcrumb = (
  category: string,
  message: string,
  data?: EventPayload,
): void => {
  if (!sentryEnabled) {
    return;
  }

  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: 'info',
  });
};

export const track = (
  eventName: string,
  payload?: EventPayload,
  tags?: EventTags,
): void => {
  if (!sentryEnabled) {
    return;
  }

  Sentry.captureMessage(eventName, {
    level: 'info',
    tags: {
      service: SERVICE_TAG,
      ...tags,
    },
    extra: payload,
  });
};

export const captureError = (
  error: unknown,
  context?: EventPayload,
  tags?: EventTags,
): void => {
  if (!sentryEnabled) {
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag('service', SERVICE_TAG);

    if (tags) {
      Object.entries(tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });
    }

    if (context) {
      scope.setContext('context', context);
    }

    const requestId = getRequestIdFromContext(context);
    if (requestId) {
      scope.setTag('request_id', requestId);
    }

    Sentry.captureException(error);
  });
};

